import React, { useEffect, useRef, useState } from "react";
import { DateRangePicker, createStaticRanges } from "react-date-range";
import { startOfDay, endOfDay, subDays, subYears } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
const DateRangeFilter = ({ filters, onChange }) => {
  const today = new Date();
  const minDate = startOfDay(subYears(today, 5));
  const pickerRef = useRef(null);
  const [showPicker, setShowPicker] = useState(false);
  const [focusedRange, setFocusedRange] = useState([0, 0]);
  const [selectionRange, setSelectionRange] = useState({
    startDate: filters?.from ? new Date(`${filters.from}T00:00:00`) : null,
    endDate: filters?.to ? new Date(`${filters.to}T00:00:00`) : null,
    key: "selection",
  });
  useEffect(() => {
    if (filters?.from && filters?.to) {
      setSelectionRange({
        startDate: new Date(`${filters.from}T00:00:00`),
        endDate: new Date(`${filters.to}T00:00:00`),
        key: "selection",
      });
    }
  }, [filters?.from, filters?.to]);
  const formatDate = (date) => {
    if (!date) {
      return "";
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const formatDisplayDate = (date) => {
    if (!date) {
      return "";
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const staticRanges = createStaticRanges([
    {
      label: "Today",
      range: () => ({
        startDate: startOfDay(new Date()),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Last 7 Days",
      range: () => ({
        startDate: startOfDay(subDays(new Date(), 6)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Last 14 Days",
      range: () => ({
        startDate: startOfDay(subDays(new Date(), 13)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Last 30 Days",
      range: () => ({
        startDate: startOfDay(subDays(new Date(), 29)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Last 90 Days",
      range: () => ({
        startDate: startOfDay(subDays(new Date(), 89)),
        endDate: endOfDay(new Date()),
      }),
    },
  ]);
  const handleChange = (ranges) => {
    const selection = ranges.selection;
    if (focusedRange[1] === 0) {
      const newSelection = {
        ...selectionRange,
        startDate: selection.startDate,
        key: "selection",
      };
      setSelectionRange(newSelection);
      setFocusedRange([0, 1]);
      return;
    }
    if (focusedRange[1] === 1) {
      const startDate = selectionRange.startDate;
      const endDate = selection.endDate;
      if (!startDate || !endDate) {
        return;
      }
      if (endDate < startDate) {
        return;
      }
      const newSelection = {
        startDate,
        endDate,
        key: "selection",
      };
      setSelectionRange(newSelection);
      onChange({
        ...filters,
        from: formatDate(startDate),
        to: formatDate(endDate),
        page: 1,
      });
      setShowPicker(false);
      setFocusedRange([0, 0]);
    }
  };
  const handleFocusChange = (nextFocusedRange) => {
    if (!nextFocusedRange) {
      return;
    }
    setFocusedRange(nextFocusedRange);
  };
  const handleClear = (event) => {
    event.stopPropagation();
    const newRange = {
      startDate: null,
      endDate: null,
      key: "selection",
    };
    setSelectionRange(newRange);
    setFocusedRange([0, 0]);
    onChange({
      ...filters,
      from: "",
      to: "",
      page: 1,
    });
    setShowPicker(false);
  };
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    if (showPicker) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showPicker]);
  const displayValue =
    selectionRange.startDate && selectionRange.endDate
      ? `${formatDisplayDate(selectionRange.startDate)} - ${formatDisplayDate(
          selectionRange.endDate,
        )}`
      : "";
  return (
    <div className="report-date-range">
      <label className="form-label fw-bold">Date Range</label>
      <div className="d-flex align-items-center gap-2">
        <div className="report-date-picker-wrapper position-relative" ref={pickerRef}>
          <div
            className="form-control report-date-input"
            onClick={() => {
              if (!showPicker) {
                setFocusedRange([0, 0]);
              }
              setShowPicker((previous) => !previous);
            }}
            role="button"
            tabIndex={0}
          >
            <span>{displayValue || "Select date range"}</span>
            {displayValue && (
              <button
                type="button"
                className="report-date-clear"
                aria-label="Clear date range"
                onClick={handleClear}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            )}
            <span className="report-calendar-icon ps-2">
              <i className="fa fa-calendar" />
            </span>
          </div>
          {showPicker && (
            <div className="report-date-picker-popup">
              <DateRangePicker
                ranges={[selectionRange]}
                onChange={handleChange}
                focusedRange={focusedRange}
                onRangeFocusChange={handleFocusChange}
                staticRanges={staticRanges}
                inputRanges={[]}
                months={1}
                direction="horizontal"
                showDateDisplay={false}
                moveRangeOnFirstSelection={false}
                rangeColors={["#0d6efd"]}
                minDate={minDate}
                maxDate={today}
                shownDate={selectionRange.startDate || new Date()}
                weekdayDisplayFormat="EE"
                monthDisplayFormat="MMMM yyyy"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default DateRangeFilter;
